import {
  ElementType,
  ChangeEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  forwardRef,
  useLayoutEffect,
  useRef,
  useState,
  RefObject,
  useEffect,
  useMemo,
} from 'react';
import { onlyText } from 'react-children-utilities';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { getTimezoneInfo } from '../../helpers';
import { CubeIconSmall, XIconSmall, CaretDownIcon, MultiLocationSmall, CaretUpIcon } from '../../icon';
import { NakedButton } from '../../naked';
import { PolymorphicComponentPropWithRef, PolymorphicComponentPropWithoutRef, PolymorphicRef } from '../../type-utils';
import { useTooltip } from '../tooltip';
import { TagChipStylesProps, ChipStylesProps, ChipStyles } from './chip.styles';
import type { CategoryChipVariants, StatusSwitcherVariants, Variants, TimezoneChipVariant } from './chip.styles';

export type ChipProps = {
  children?: ReactNode;
  onClick?: MouseEventHandler<HTMLDivElement>;
  variant?: Variants;
  className?: string;
  leftElement?: ReactNode;
  rightElement?: ReactNode;
  size?: ChipStylesProps['size'];
  editable?: boolean;
  onKeyDown?: KeyboardEventHandler;
  onChange?: ChangeEventHandler;
  trackingId?: string;
  /**
   * ***isResponsive*** should be passed when the chip is part of a container that can get smaller than 120px (such as table cells)
   * Ideally we wouldn't need to pass this prop, but there seems to be a weird behavior if `min()` is used inside `max-width`;
   */
  isResponsive?: boolean;

  /**
   * **containerRef** use in conjuction with isResponsive. When you want the chip to be responsive and show
   * as much text as possible, but respect a max-width. This usually breaks in a flex box, so the containerRef
   * tells it how big it's "max" is.
   */
  containerRef?: RefObject<HTMLElement>;

  maxWidth?: string;
  /**
   * ***hideTooltip***
   * By default when the chip's text is too long it will show a tooltip with the full text on hover.
   * This prop allows the consumer to hide the tooltip and will allow the consumer to use its own tooltip implementation.
   */
  hideTooltip?: boolean;
  tooltipLabel?: string | ReactNode;
};

type PolymorphicChipProps<T extends ElementType> = PolymorphicComponentPropWithRef<T, ChipProps>;

type PolymorphicChipType = (<C extends ElementType = 'div'>(props: PolymorphicChipProps<C>) => React.ReactNode) & {
  displayName?: string;
};

const commonEditableStyles = {
  width: 'auto',
  minWidth: '1em',
  gridArea: '1',
  font: 'inherit',
  padding: 0,
  margin: '0',
  resize: 'none',
  background: 'none',
  appearance: 'none',
  border: 'none',
} as const;

export const ChipOptional: PolymorphicChipType = forwardRef(
  <T extends ElementType = 'div'>(
    {
      as,
      children,
      variant = 'primary',
      leftElement,
      rightElement,
      size = 'default',
      trackingId,
      className,
      editable,
      onChange,
      onKeyDown,
      isResponsive,
      maxWidth = '120px',
      hideTooltip,
      tooltipLabel,
      containerRef,
      ...rest
    }: PolymorphicChipProps<T> & { maxWidth: string },
    ref: PolymorphicRef<T>
  ) => {
    const Component = as || 'div';
    const [containerWidth, setContainerWidth] = useState<number | false>(false);
    const chipStyles = ChipStyles.chip({
      variant,
      size,
      isResponsive,
      maxWidth: containerWidth ? `${containerWidth}px` : maxWidth,
    });

    const [showTooltip, setShowTooltip] = useState(false);
    const [offsetWidth, setOffsetWidth] = useState<number | undefined>(undefined);
    const {
      Tooltip,
      triggerProps: { ref: triggerRef, ...restTriggerProps },
      tooltipProps,
    } = useTooltip({ initialOpen: false, trigger: 'hover' });
    const spanRef = useRef<HTMLSpanElement | null>(null);

    useLayoutEffect(() => {
      if (!spanRef.current) return;
      // need to trigger a rerender to get the width of the element after it has been rendered
      // otherwise the width will be 0 until a rerender is triggered
      setOffsetWidth(spanRef?.current?.offsetWidth);
    }, [spanRef?.current?.offsetWidth]);

    useEffect(() => {
      if (!containerRef?.current || !isResponsive) {
        return;
      }

      const resizeObserver = new ResizeObserver(() => {
        if (containerRef.current) {
          const width = containerRef.current.clientWidth;
          setContainerWidth(width);
        }
      });
      resizeObserver.observe(containerRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }, [isResponsive]);

    function chipMeasureRef(node: HTMLSpanElement) {
      if (node === null) return;

      spanRef.current = node;
      if (!offsetWidth) return;
      if (offsetWidth < node.scrollWidth && !hideTooltip) {
        setShowTooltip(true);
        triggerRef(node);
      } else {
        setShowTooltip(false);
      }
    }

    const tooltipText = tooltipLabel || onlyText(children);

    return (
      <Component
        // Ensure that className passed does not override the .chip class
        className={'chip' + (className ? ` ${className}` : '')}
        css={[
          chipStyles,
          editable && {
            width: 'max-content',
          },
        ]}
        {...rest}
        data-trackingid={trackingId}
        {...(hideTooltip ? {} : restTriggerProps)}
        ref={tooltipLabel ? triggerRef : ref}
      >
        {leftElement}
        {editable ? (
          <span
            ref={chipMeasureRef}
            css={{
              display: 'inline-grid',
              '::after': {
                content: 'attr(data-value)',
                visibility: 'hidden',
                whiteSpace: 'pre-wrap',
                height: 1,
                ...commonEditableStyles,
              },
            }}
          >
            <input
              autoFocus
              size={1}
              css={{
                ...commonEditableStyles,
                ':focus': {
                  border: 'none',
                  // The outline will be around the chip component instead of the input
                  outline: 'none',
                },
              }}
              className='chip-input'
              {...(hideTooltip ? {} : restTriggerProps)}
              onKeyDown={onKeyDown}
              onChange={(e) => {
                if (e.currentTarget.parentElement) {
                  e.currentTarget.parentElement.dataset.value = e.currentTarget.value;
                }
                onChange?.(e);
              }}
            />
          </span>
        ) : (
          <span
            className='chip-text'
            {...(hideTooltip ? {} : restTriggerProps)}
            ref={tooltipLabel ? triggerRef : chipMeasureRef}
            css={{ fontWeight: '400' }}
          >
            {children}
          </span>
        )}
        {(showTooltip || tooltipLabel) && tooltipText && <Tooltip {...tooltipProps}>{tooltipText}</Tooltip>}
        {rightElement}
      </Component>
    );
  }
);

ChipOptional.displayName = 'ChipOptional';

const RemovableChip = ({
  onClick,
  disabled,
  variant,
  ...props
}: ChipProps & { onClick: MouseEventHandler<HTMLButtonElement>; disabled?: boolean }) => {
  return (
    <ChipOptional
      variant={disabled ? 'disabled' : variant ? variant : 'interactive'}
      {...props}
      rightElement={
        <NakedButton
          disabled={disabled}
          onClick={onClick}
          css={{
            display: 'flex',
            flexShrink: 0,
            padding: theme.spacing(0.5),
            borderRadius: theme.borderRadius.small,
            pointerEvents: disabled ? 'none' : 'auto',
            ':hover': { background: theme.colors.white },
          }}
        >
          <Icon size={8} name='x-tiny' color={disabled ? 'disabled' : 'default'} />
        </NakedButton>
      }
    />
  );
};

export const PetChip = forwardRef<HTMLDivElement, { leftElement: React.ReactNode; name: string }>(
  ({ leftElement, name, ...rest }, ref) => (
    <ChipOptional variant='neutral' leftElement={leftElement} ref={ref} {...rest}>
      {name}
    </ChipOptional>
  )
);
PetChip.displayName = 'PetChip';

const PersonChip = ({
  onClick,
  variant = 'outline',
  ...props
}: ChipProps & { avatar: ReactNode; onClick?: MouseEventHandler<HTMLButtonElement> }) => {
  return onClick ? (
    <RemovableChip onClick={onClick} {...props} variant='outline' leftElement={props.avatar} />
  ) : (
    <ChipOptional variant={variant} {...props} leftElement={props.avatar} />
  );
};

const LocationChip = (props: ChipProps) => {
  return (
    <ChipOptional {...props} variant={props.variant ?? 'default'} leftElement={<Icon name='location' size={16} />} />
  );
};

const HouseholdChip = (props: ChipProps) => {
  return <ChipOptional {...props} variant={props.variant ?? 'default'} leftElement={<Icon name='crown' size={14} />} />;
};

const SourceChip = (props: ChipProps) => {
  return (
    <ChipOptional {...props} variant={props.variant ?? 'default'} leftElement={<Icon name='data-sync' size={16} />} />
  );
};

const SingleChip = (props: ChipProps) => {
  return <ChipOptional {...props} variant='primaryDark' leftElement={<Icon name='location' size={16} />} />;
};

const MultiChip = (props: ChipProps) => {
  return <ChipOptional {...props} variant='warningDark' leftElement={<MultiLocationSmall size={16} />} />;
};

const DepartmentChip = (props: ChipProps) => {
  return <ChipOptional {...props} variant='default' leftElement={<CubeIconSmall size={16} />} />;
};

const TimeZoneChip = ({
  children,
  hideIcon = false,
  variant = 'neutral',
  ...rest
}: Omit<ChipProps, 'children' | 'variant'> & {
  hideIcon?: boolean;
  children: string;
  variant?: TimezoneChipVariant;
}) => {
  const { shortText, longText, getDate } = useMemo(() => getTimezoneInfo(children), [children]);
  const timezoneChipStyles = ChipStyles.timezoneChip({ variant });
  const [tooltipText, setTooltipText] = useState(longText);

  const handleMouseEnter = () => setTooltipText(`${getDate().format('hh:mm A')} ${longText}`.trim());

  if (!shortText) {
    return null;
  }

  return (
    <ChipOptional
      {...rest}
      onMouseEnter={handleMouseEnter}
      tooltipLabel={tooltipText}
      leftElement={!hideIcon && <Icon name='network-small' color='default' size={14} />}
      css={timezoneChipStyles}
    >
      {shortText}
    </ChipOptional>
  );
};

export type TagChipProps<C extends MouseEventHandler<HTMLButtonElement> | undefined> = Omit<ChipProps, 'variant'> & {
  color: TagChipStylesProps['color'];
  onClick?: C;
  onRemoveClick?: MouseEventHandler<HTMLButtonElement>;
  isRemovable?: boolean;
  trackingIds?: Partial<{
    chip: string;
    removeButton: string;
  }>;
  size?: string;
};

const TagChip = forwardRef(
  <
    C extends MouseEventHandler<HTMLButtonElement> | undefined,
    E extends ElementType = C extends undefined ? 'div' : 'button'
  >(
    {
      color,
      onClick,
      isRemovable,
      onRemoveClick,
      as,
      trackingIds,
      size,
      ...props
    }: PolymorphicComponentPropWithoutRef<E, TagChipProps<C>>,
    ref?: PolymorphicRef<E>
  ) => {
    const tagStyles = ChipStyles.tagChip({ color, hasOnClick: !!onClick });

    const resolvedAs = as || !!onClick ? 'button' : 'div';

    return (
      <ChipOptional
        as={resolvedAs}
        onClick={onClick}
        css={tagStyles}
        rightElement={
          isRemovable && onRemoveClick ? (
            <NakedButton
              onClick={(e) => {
                e.stopPropagation();
                onRemoveClick(e);
              }}
              css={{
                display: 'flex',
              }}
              trackingId={trackingIds?.removeButton}
            >
              <XIconSmall size={size === 'tiny' ? 10 : 13} />
            </NakedButton>
          ) : undefined
        }
        ref={ref}
        size={size}
        trackingId={trackingIds?.chip}
        {...props}
      />
    );
  }
);
TagChip.displayName = 'TagChip';

type DropdownFilterChipProps = Omit<ChipProps, 'variant' | 'size' | 'rightElement' | 'onClick'> & {
  filterIsActive?: boolean;
  onClick?: MouseEventHandler;
  isOpen?: boolean;
};

const DropdownFilterChip = forwardRef<HTMLButtonElement, DropdownFilterChipProps>(
  ({ filterIsActive = false, isOpen = false, ...props }, ref) => {
    const dropdownFilterStyles = ChipStyles.dropdownFilterChip({ filterIsActive, isOpen });

    return (
      <ChipOptional
        as='button'
        css={dropdownFilterStyles}
        size='large'
        rightElement={<Icon className='location-caret' name='caret-down-tiny' size={12} />}
        {...props}
        ref={ref}
      />
    );
  }
);
DropdownFilterChip.displayName = 'DropdownFilterChip';

export type StatusSwitcherChipProps = Omit<ChipProps, 'variant' | 'size' | 'rightElement' | 'onClick'> & {
  disabled?: boolean;
  isOpen?: boolean;
  variant: StatusSwitcherVariants;
};

const StatusSwitcherChip = forwardRef<HTMLButtonElement, StatusSwitcherChipProps>(
  ({ disabled = false, isOpen = false, variant = 'default', ...props }, ref) => {
    const statusSwitcherStyles = ChipStyles.statusSwitcherChip({ variant: disabled ? 'disabled' : variant });

    return (
      <ChipOptional
        as='button'
        css={statusSwitcherStyles}
        disabled={disabled}
        rightElement={isOpen ? <CaretUpIcon size={8} /> : <CaretDownIcon size={8} />}
        {...props}
        ref={ref}
      />
    );
  }
);
StatusSwitcherChip.displayName = 'StatusSwitcherChip';

export type CategoryChipProps = Omit<ChipProps, 'variant' | 'size' | 'rightElement' | 'onClick' | 'maxWidth'> & {
  variant?: CategoryChipVariants;
  indicatorColor?: string;
};

const CategoryChip = forwardRef<HTMLDivElement, CategoryChipProps>(
  ({ variant = 'neutral', indicatorColor, ...props }, ref) => {
    const categoryChipStyles = ChipStyles.categoryChip({ variant, indicatorColor });

    return (
      <ChipOptional
        css={categoryChipStyles}
        leftElement={<span className='category-chip-indicator' />}
        ref={ref}
        {...props}
      />
    );
  }
);

CategoryChip.displayName = 'CategoryChip';

type TaskChipProps = Omit<ChipProps, 'variant'> & {
  onClick: MouseEventHandler<HTMLButtonElement>;
  variant: 'task' | 'done';
  type?: 'manual' | 'auto';
  tasks?: number;
  iconSize?: 'tiny' | 'small';
};
const TaskChip = ({
  onClick,
  tasks = 0,
  variant = 'task',
  type = 'manual',
  iconSize = 'small',
  ...props
}: TaskChipProps) => {
  const taskChipStyles = ChipStyles.taskChip({ variant });
  return (
    <ChipOptional
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      {...props}
      leftElement={
        !!tasks ? (
          <span>{tasks < 100 ? tasks : '99+'}</span>
        ) : (
          <Icon name={variant === 'task' ? 'pending-small' : 'check-small'} />
        )
      }
      rightElement={
        <span css={{ display: 'flex' }}>
          {type === 'auto' && <Icon name='aiassistant-small' />}
          <Icon name={`caret-right-${iconSize}`} />
        </span>
      }
      css={taskChipStyles}
    />
  );
};

type SwitchFilterChipType = ChipProps & {
  onClick: MouseEventHandler<HTMLButtonElement>;
  selected?: boolean;
  disabled?: boolean;
};

const SwitchFilterChip = ({ onClick, selected = false, disabled = false, ...props }: SwitchFilterChipType) => {
  const switchFilterChipStyles = ChipStyles.switchFilterChip(selected);
  return (
    <ChipOptional
      as='button'
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      {...props}
      css={switchFilterChipStyles}
    />
  );
};

const ChipNamespace = Object.assign(ChipOptional, {
  Removable: RemovableChip,
  Location: LocationChip,
  DataSource: SourceChip,
  Department: DepartmentChip,
  Person: PersonChip,
  SingleChip: SingleChip,
  MultiChip: MultiChip,
  TimeZone: TimeZoneChip,
  Tag: TagChip,
  Pet: PetChip,
  DropdownFilter: DropdownFilterChip,
  StatusSwitcher: StatusSwitcherChip,
  Household: HouseholdChip,
  Category: CategoryChip,
  SwitchFilter: SwitchFilterChip,
  Task: TaskChip,
});

export { ChipNamespace as Chip };
